import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Réponse générique d'une action (appel / SMS / archivage). */
export interface RingoverActionResponse {
  status: string;
  status_code: number;
  data?: any;
}

/** Payload d'envoi de SMS. */
export interface SmsRequest {
  to_number: string;          // E.164, ex +33612345678
  content: string;
  from_number?: string;       // sinon numéro par défaut du user
  archived_auto?: boolean;
  user_id_forced?: number;    // Monitoring ON
  scheduled_at?: string;      // ISO 8601 UTC
}

/** Code appareil à faire sonner pour le callback (3 lettres). */
export type RingoverDevice = 'ALL' | 'APP' | 'WEB' | 'SIP' | 'MOB' | 'EXT';

/** Payload de lancement d'appel (callback / click-to-call). */
export interface CallbackRequest {
  to_number: string;          // E.164
  from_number?: string;       // Monitoring ON
  device?: RingoverDevice;    // défaut ALL
  timeout?: number;           // 10..300, défaut 20
  clir?: boolean;             // masquer le numéro appelant
}

/** Payload d'appel d'un contact CRM avec suivi automatique du statut (NRP). */
export interface CallContactRequest extends CallbackRequest {
  contact_id: number;
}

/** Payload d'armement du suivi NRP après un click-to-call `tel:`.
 *  Cible : `contact_id` direct, OU `project_id` (appel depuis le formulaire de
 *  comparaison) — le serveur résout alors la transaction liée et son contact. */
export interface TrackCallRequest {
  contact_id?: number;
  project_id?: string;
  to_number: string;
  transaction_id?: number;   // transaction à mettre à jour (resultat_appel) ; sinon la plus récente
}

/** Payload d'armement du suivi NRP d'un RDV agenda après un click-to-call `tel:`. */
export interface TrackAgendaCallRequest {
  agenda_project_id: number;
  to_number: string;
}

/** Réponse de l'armement du suivi NRP : diagnostic immédiat de la permission Calls Read. */
export interface TrackCallResponse {
  status: string;        // "tracking" | "skipped"
  contact_id: number;
  calls_read: boolean;   // false = permission Calls Read manquante
  message: string;
}

/** Paramètres de listing des appels terminés. */
export interface ListCallsParams {
  start_date?: string;
  end_date?: string;
  limit_count?: number;       // 1..1000
  limit_offset?: number;      // 0..9000
  last_id_returned?: number;
  call_type?: string[];       // ANSWERED | MISSED | OUT | VOICEMAIL
}

@Injectable({
  providedIn: 'root'
})
export class RingoverService {

  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/ringover`;

  constructor(private http: HttpClient) { }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /** Lance un appel (callback) : Ringover rappelle l'appelant puis compose le destinataire. */
  call(payload: CallbackRequest): Observable<RingoverActionResponse> {
    return this.http.post<RingoverActionResponse>(`${this.apiUrl}/call`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('call')));
  }

  /** Appelle un contact CRM (callback) et arme le suivi : passage auto en "NRP"
   *  côté serveur si l'appel se termine sans réponse. */
  callContact(payload: CallContactRequest): Observable<RingoverActionResponse> {
    return this.http.post<RingoverActionResponse>(`${this.apiUrl}/call-contact`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('callContact')));
  }

  /** Arme le suivi NRP après un click-to-call `tel:` (ne déclenche aucun appel).
   *  Le serveur surveille /calls et passe le contact en "NRP" si non-réponse. */
  trackCall(payload: TrackCallRequest): Observable<TrackCallResponse> {
    return this.http.post<TrackCallResponse>(`${this.apiUrl}/track-call`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('trackCall')));
  }

  /** Arme le suivi NRP d'un RDV agenda après un click-to-call `tel:` : le serveur
   *  surveille /calls et passe le statut lead du RDV en "NRP" si non-réponse. */
  trackAgendaCall(payload: TrackAgendaCallRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/track-call-agenda`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('trackAgendaCall')));
  }

  /** Historique SMS Ringover pour un numéro (permission Conversations Read). */
  getSmsHistory(phone: string, limitCount = 50): Observable<{ messages: any[]; total: number }> {
    const params = new HttpParams()
      .set('phone', phone)
      .set('limit_count', limitCount.toString());
    return this.http.get<any>(`${this.apiUrl}/sms-history`, {
      params,
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError('getSmsHistory')));
  }

  /** Envoie un SMS. */
  sendSms(payload: SmsRequest): Observable<RingoverActionResponse> {
    return this.http.post<RingoverActionResponse>(`${this.apiUrl}/sms`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('sendSms')));
  }

  /** Liste les appels terminés (du plus récent au plus ancien). */
  listCalls(params: ListCallsParams = {}): Observable<any> {
    let httpParams = new HttpParams();
    if (params.start_date) httpParams = httpParams.set('start_date', params.start_date);
    if (params.end_date) httpParams = httpParams.set('end_date', params.end_date);
    if (params.limit_count != null) httpParams = httpParams.set('limit_count', params.limit_count);
    if (params.limit_offset != null) httpParams = httpParams.set('limit_offset', params.limit_offset);
    if (params.last_id_returned != null) httpParams = httpParams.set('last_id_returned', params.last_id_returned);
    (params.call_type || []).forEach(t => { httpParams = httpParams.append('call_type', t); });

    return this.http.get<any>(`${this.apiUrl}/calls`, {
      headers: this.getAuthHeaders(),
      params: httpParams
    }).pipe(catchError(this.handleError('listCalls')));
  }

  /** Liste les appels avec filtres avancés (filter=ADVANCED, tags, users, IVRs…). */
  searchCalls(body: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/search`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('searchCalls')));
  }

  /** Liste les appels en cours (live). */
  currentCalls(body: any = {}): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls/current`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('currentCalls')));
  }

  /** Détail d'un appel par son ID. */
  getCall(callId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/calls/${callId}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('getCall')));
  }

  /** Archive un appel (soft-delete) par son ID. */
  archiveCall(callId: string): Observable<RingoverActionResponse> {
    return this.http.delete<RingoverActionResponse>(`${this.apiUrl}/calls/${callId}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError('archiveCall')));
  }

  private handleError(op: string) {
    return (error: any) => {
      console.error(`RingoverService.${op} error:`, error);
      return throwError(() => error);
    };
  }
}
